import {
  Actions,
  ResultStatus,
  Subjects,
  TaskGroup,
  TaskGroupDispatchMethod,
} from '@dispatch/shared/types';
import { Ability } from '@casl/ability';
import {
  DrawerComponent,
  Form,
  RenderFuncProps,
  Section,
  SectionDivider,
  SelectField,
  TextField,
} from '@dispatch/web/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Stack } from '@mui/material';
import { useForm } from 'react-hook-form';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

const schema = yup
  .object({
    name: yup.string().trim().required('Task Group Name required'),
    description: yup.string().required('Task Group Description required'),
    dispatchMethod: yup
      .mixed<TaskGroupDispatchMethod>()
      .oneOf(['Manual', 'Optimized', 'RoundRobin']),
  })
  .required();
export type TaskGroupsDetailsProps = {
  data?: TaskGroup;
  status: ResultStatus;
  onSave: (value: TaskGroup) => void;
  onActivate?: (taskGroup: TaskGroup) => void;
  onDeactivate?: (taskGroup: TaskGroup) => void;
  permissions: Ability<[Actions, Subjects]> | undefined;
};

export function TaskGroupDetails({
  data,
  onSave,
  status,
  onActivate,
  onDeactivate,
  permissions,
}: TaskGroupsDetailsProps) {
  const navigate = useNavigate();

  const formDefaults: TaskGroup = {
    id: '',
    name: '',
    description: '',
    tenantId: '',
    dispatchMethod: 'Optimized',
    dispatchConfigurations: [],
    userIds: [],
    deactivatedAt: '',
  };

  const {
    control: formControl,
    formState: { errors: formErrors, isDirty },
    handleSubmit,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { ...formDefaults },
  });

  useEffect(() => {
    if (data === undefined) return;
    reset({
      id: data.id,
      name: data.name,
      description: data.description,
      accessList: data.accessList,
      tenantId: data.tenantId,
      dispatchMethod: data.dispatchMethod,
      createdBy: data.createdBy,
      createdDate: data.createdDate,
      lastEditedBy: data.lastEditedBy,
      lastEditedDate: data.lastEditedDate,
      dispatchConfigurations: data.dispatchConfigurations,
    });
  }, [data, reset]);

  const dispatchMethodOptions: {
    title: string;
    value: TaskGroupDispatchMethod;
  }[] = [
    { title: 'Manual', value: 'Manual' },
    { title: 'Optimized', value: 'Optimized' },
    { title: 'Round Robin', value: 'RoundRobin' },
  ];

  const handleSave = () => {
    handleSubmit(onSave)();
  };

  const handleDeactivation = () => {
    if (!data) return;
    onDeactivate?.(data);
  };

  const handleActivation = () => {
    if (!data) return;
    onActivate?.(data);
  };

  const formArgs = {
    render: ({ control, errors }: RenderFuncProps) => {
      return (
        <>
          <Section>
            <Grid container spacing={2}>
              <Grid size={3}>
                <TextField
                  id="name"
                  name="name"
                  label="Name"
                  required
                  control={control}
                  errors={errors}
                  sx={{ width: '100%' }}
                />
              </Grid>
              <Grid size={9} />

              <Grid size={10}>
                <TextField
                  id="description"
                  name="description"
                  label="Description"
                  required
                  control={control}
                  errors={errors}
                  sx={{ width: '100%' }}
                  multiline
                />
              </Grid>
            </Grid>
            <Stack direction="column" spacing={2}></Stack>
          </Section>
          <SectionDivider />
          <Section title="Rules">
            <Grid container spacing={2} data-test-id="rules">
              <Grid size={3}>
                <SelectField
                  id={`dispatchMethod`}
                  name={`dispatchMethod`}
                  label="Dispatch Method"
                  required
                  control={control}
                  errors={errors}
                  error={
                    errors['dispatchMethod']
                      ? errors['dispatchMethod']?.message
                      : null
                  }
                  options={dispatchMethodOptions}
                />
              </Grid>
              <Grid size={9} />
            </Grid>
          </Section>
        </>
      );
    },
  };

  return (
    <DrawerComponent
      onSave={handleSave}
      saveDisabled={!isDirty}
      showActivateButton={!!data?.deactivatedAt}
      showDeactivateButton={!data?.deactivatedAt}
      showButtons={permissions?.can?.('upsert', 'task-group')}
      onActivate={handleActivation}
      onDeactivate={handleDeactivation}
      onClose={() => navigate('/admin/task-groups')}
      title={data?.name || 'New Task Group'}
      subtitle="TASK GROUP"
      isActive={!data?.deactivatedAt}
      status={status}
    >
      {data && (
        <Form<TaskGroup>
          {...formArgs}
          errors={formErrors}
          control={formControl}
          handleSubmit={handleSubmit}
        />
      )}
    </DrawerComponent>
  );
}
